import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Button, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Can from 'components/Can';
import Layout from 'components/Layout';
import Comments from 'components/Comments';
import useStyles from './UserGroupDetails.styled';
import { withRouter, useParams } from 'react-router-dom';
import DocumentsWidget from 'components/Documents/Widget';
import { userGroupsActions } from 'state/modules/userGroups';
import { Details, UserGroupsLogs, ParentGroups } from 'components/UserGroups';
import { ChangeParentGroupsModal } from 'components/Modals';

const UserGroupDetails = ({ history }) => {
  const classes = useStyles();
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    userGroup,
    fetchedAddItem,
    fetchedRemoveParent,
    onboarding: { steps },
  } = useSelector((state) => state.userGroups);
  const [openParentGroupsModal, setOpenParentGroupsModal] = useState(false);
  const [currentParentsParams] = useState({
    page: 1,
    limit: 3,
  });

  const fetchParents = (params = {}) => {
    dispatch(userGroupsActions.fetchParents(id, params, 'parents', 'parentsModal'));
  };

  useEffect(() => {
    if (id) {
      fetchParents(currentParentsParams);
      dispatch(userGroupsActions.fetchUserGroupById(id));
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (fetchedAddItem || fetchedRemoveParent) {
      fetchParents(currentParentsParams);
    }
  }, [fetchedAddItem, fetchedRemoveParent]); // eslint-disable-line

  /**
   * Go to user groups list page
   */
  const goToUserGroupsList = () => {
    history.push(buildUrl('/users-groups'));
  };

  /**
   * Go to create user group page
   */
  const goToCreateUserGroup = () => {
    history.push(buildUrl('/users-groups/create'));
  };

  const handleUpdateParentModalOpen = () => {
    setOpenParentGroupsModal((prevState) => !prevState);
  };

  const parentGroupsItems = [
    {
      title: i18n.t('user_groups.parent.edit_parent_groups'),
      onClick: handleUpdateParentModalOpen,
      can: { action: 'update', name: 'user_group' },
    },
  ];

  const description = userGroup?.name || '';

  return (
    <Layout title="User Group">
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container direction="row" justifyContent="space-between" spacing={2}>
              <Grid item>
                <Typography variant="h1" gutterBottom>
                  <Box fontWeight="fontWeightBold" fontSize={24} mb={1}>
                    {i18n.t('common.detail_title', { description, id })}
                  </Box>
                </Typography>
              </Grid>
              <Grid item>
                <Can I="read" a="user_group">
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={goToUserGroupsList}
                    style={{ textTransform: 'inherit' }}
                  >
                    {i18n.t('user_group.see_all_user_groups')}
                  </Button>
                </Can>
                <Can I="create" a="user_group">
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={goToCreateUserGroup}
                    style={{ textTransform: 'inherit', marginLeft: 10 }}
                  >
                    {i18n.t('user_group.add_user_group')}
                  </Button>
                </Can>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Details id={parseInt(id)} />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <ParentGroups
                          parentGroups={steps?.parents?.parentsModal?.list}
                          menuOptions={parentGroupsItems}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Comments type="user_group" id={parseInt(id)} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Can I="read" a="document">
                <Grid item xs={12} sm={12} md={4}>
                  <DocumentsWidget type="user_group" id={parseInt(id)} />
                </Grid>
              </Can>
              {userGroup?.user_group_type && (
                <Can I="read" on="event_log">
                  <Grid item xs={12} sm={12} md={4}>
                    <UserGroupsLogs id={id} type={userGroup?.user_group_type} />
                  </Grid>
                </Can>
              )}
            </Grid>
          </Grid>
        </Grid>
        {openParentGroupsModal && (
          <ChangeParentGroupsModal
            open={openParentGroupsModal}
            userGroup={userGroup}
            steps={steps}
            handleClick={handleUpdateParentModalOpen}
          />
        )}
      </Container>
    </Layout>
  );
};

export default withRouter(UserGroupDetails);
